/**
 * Status — Spec 020 D3, DS `Status` (variant `pill`).
 *
 * One set for the whole product, the word always shown: a dot of the status
 * colour and the word, on the status tint. The emoji ● ✅ 🧪 ❗ ⛔ ⏳ are the
 * bot's language and stay there. No other word is a status: a property of a
 * record (a draft, an archived method, a rejected idea) is a `Mark` — a quiet
 * word with no colour, so it cannot pass for one.
 */

import type { ReactNode } from "react";

import { cn } from "@/lib/cn";

export type StatusTone = "now" | "done" | "review" | "needs" | "blocked" | "waiting";

export const STATUS_LABEL: Record<StatusTone, string> = {
  now: "Сейчас",
  done: "Сделано",
  review: "Готово к проверке",
  needs: "Нужно от тебя",
  blocked: "Препятствие",
  waiting: "Ждёт",
};

/** Yellow only here and on the one main button (D2). */
const TONE: Record<StatusTone, string> = {
  now: "bg-p-now text-p-on-now",
  done: "bg-p-done-tint text-p-done",
  review: "bg-p-review-tint text-p-review",
  needs: "bg-p-needs-tint text-p-needs",
  blocked: "bg-p-blocked-tint text-p-blocked",
  waiting: "bg-p-waiting-tint text-p-waiting",
};

export function Status({ tone, className }: { tone: StatusTone; className?: string }) {
  return (
    <span
      className={cn(
        "inline-flex h-6 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 text-[13px] font-semibold leading-4",
        TONE[tone],
        className,
      )}
    >
      <span aria-hidden="true" className="h-[7px] w-[7px] shrink-0 rounded-full bg-current" />
      {STATUS_LABEL[tone]}
    </span>
  );
}

/** A property of a record, not a status: muted text, an optional icon, no pill. */
export function Mark({ children, icon, className }: { children: ReactNode; icon?: ReactNode; className?: string }) {
  return (
    <span className={cn("inline-flex shrink-0 items-center gap-1 text-meta text-p-text-muted", className)}>
      {icon}
      {children}
    </span>
  );
}
